import {useEffect, useMemo, useRef, useState} from 'react'
import type {KeyboardEvent as ReactKeyboardEvent} from 'react'
import {ChevronLeft, ChevronRight} from 'lucide-react'
import {
    addDays,
    addMonths,
    addYears,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    parseISO,
    startOfMonth,
    startOfWeek,
    subMonths,
    subYears,
} from 'date-fns'
import {cn} from '@/lib/utils'
import {Button} from '@/components/ui/button'
import {useAnnouncer} from '@/components/shared/Announcer'

/**
 * Lightweight month-grid calendar backed by `date-fns` and native `Date`.
 * Used by filter controls that previously rendered `<input type="date">`.
 * Renders any date regardless of past/future — filter controls accept
 * unclamped ranges per spec §4.3. `disabled` is intentionally unsupported:
 * filter callers have no legitimate reason to constrain the range.
 *
 * The calendar emits ISO-date strings (`YYYY-MM-DD`) via `onSelect` — callers
 * write that string straight into the URL, matching the native picker's
 * shape. For `<480px` mobile viewports callers fall back to native to keep
 * the touch affordance spec-§5.8 requires.
 */
export interface CalendarProps {
    /** ISO date string (`YYYY-MM-DD`) or empty. */
    value: string
    /** Fires with an ISO date string (`YYYY-MM-DD`). */
    onSelect(iso: string): void
    /** i18n label used for the month / year navigation buttons. */
    labels: CalendarLabels
    'data-testid'?: string
}

export interface CalendarLabels {
    readonly prevMonth: string
    readonly nextMonth: string
    readonly prevYear: string
    readonly nextYear: string
    /**
     * Issue 342.26 — labels for the year-quick-jump UI. Clicking the
     * month-year header switches the grid to a 4×3 year selector so users
     * picking a far-past date (e.g. birth year 1996 from 2026) skip the
     * 30-click chevron walk.
     */
    readonly pickYear: string
    readonly backToDays: string
    readonly prevDecade: string
    readonly nextDecade: string
    /** Accessible header for the weekday row (e.g., "Weekdays"). */
    readonly weekdayHeader: string
    /** Short weekday names, Sunday-first, length 7 (e.g., ['Sun', 'Mon', …]). */
    readonly weekdaysShort: ReadonlyArray<string>
}

const WEEKS_IN_GRID = 6
const DAYS_IN_WEEK = 7
/** Issue 342.26 — year-grid layout: 4 columns × 3 rows = 12 years per page. */
const YEARS_PER_PAGE = 12
const YEAR_GRID_COLUMNS = 4
const YEAR_GRID_ROWS = 3

function parseValue(v: string): Date | null {
    if (!v) return null
    try {
        const d = parseISO(v)
        return Number.isNaN(d.getTime()) ? null : d
    } catch {
        return null
    }
}

function toIso(d: Date): string {
    return format(d, 'yyyy-MM-dd')
}

type CalendarView = 'days' | 'years'

export function Calendar({value, onSelect, labels, 'data-testid': testId}: CalendarProps) {
    const selected = useMemo(() => parseValue(value), [value])
    const [visibleMonth, setVisibleMonth] = useState<Date>(() => selected ?? new Date())
    const [view, setView] = useState<CalendarView>('days')
    const {announce} = useAnnouncer()

    const gridDays = useMemo(() => {
        const first = startOfWeek(startOfMonth(visibleMonth))
        const last = endOfWeek(endOfMonth(visibleMonth))
        const days: Date[] = []
        const cursor = new Date(first)
        while (cursor <= last) {
            days.push(new Date(cursor))
            cursor.setDate(cursor.getDate() + 1)
        }
        // Pad to 6 weeks so the grid height is stable month-to-month.
        while (days.length < WEEKS_IN_GRID * DAYS_IN_WEEK) {
            const extra = new Date(days[days.length - 1])
            extra.setDate(extra.getDate() + 1)
            days.push(extra)
        }
        return days
    }, [visibleMonth])

    const today = new Date()

    /* WHY: the chevron buttons keep focus on themselves while the visible
       month silently changes. Without an announcement, a screen-reader
       user has no idea where the calendar moved to. Announce the new
       MMMM yyyy via the polite live region every time the visible month
       advances. */
    const announceMonth = (next: Date) => announce(format(next, 'MMMM yyyy'))

    const goPrevMonth = () => { const next = subMonths(visibleMonth, 1); setVisibleMonth(next); announceMonth(next) }
    const goNextMonth = () => { const next = addMonths(visibleMonth, 1); setVisibleMonth(next); announceMonth(next) }
    const goPrevYear = () => { const next = subYears(visibleMonth, 1); setVisibleMonth(next); announceMonth(next) }
    const goNextYear = () => { const next = addYears(visibleMonth, 1); setVisibleMonth(next); announceMonth(next) }
    const goToMonth = (next: Date) => { setVisibleMonth(next); announceMonth(next) }

    return (
        <div className="p-3" data-testid={testId}>
            {view === 'days' ? (
                <DayView
                    visibleMonth={visibleMonth}
                    selected={selected}
                    today={today}
                    gridDays={gridDays}
                    labels={labels}
                    onSelect={onSelect}
                    onPrevYear={goPrevYear}
                    onPrevMonth={goPrevMonth}
                    onNextMonth={goNextMonth}
                    onNextYear={goNextYear}
                    onGoToMonth={goToMonth}
                    onPickYear={() => setView('years')}
                />
            ) : (
                <YearView
                    visibleMonth={visibleMonth}
                    selectedYear={selected ? selected.getFullYear() : null}
                    todayYear={today.getFullYear()}
                    labels={labels}
                    onShiftDecade={(delta) => setVisibleMonth(d => withYear(d, d.getFullYear() + delta))}
                    onPickYear={(year) => {
                        const next = withYear(visibleMonth, year)
                        setVisibleMonth(next)
                        setView('days')
                        announceMonth(next)
                    }}
                    onBackToDays={() => setView('days')}
                />
            )}
        </div>
    )
}

interface DayViewProps {
    visibleMonth: Date
    selected: Date | null
    today: Date
    gridDays: Date[]
    labels: CalendarLabels
    onSelect: (iso: string) => void
    onPrevYear: () => void
    onPrevMonth: () => void
    onNextMonth: () => void
    onNextYear: () => void
    onGoToMonth: (next: Date) => void
    onPickYear: () => void
}

function DayView({
    visibleMonth, selected, today, gridDays, labels, onSelect,
    onPrevYear, onPrevMonth, onNextMonth, onNextYear, onGoToMonth, onPickYear,
}: DayViewProps) {
    /* WHY: W3C ARIA grid roving-tabindex pattern. Exactly one day cell carries
       tabindex=0; arrow keys move the active cell, Tab exits the grid. Active
       date defaults to the selected date when the visible month contains it,
       otherwise today (if visible), otherwise the first day of the visible
       month. Crossing a month boundary via arrow / Page keys updates the
       visible month and announces it via the parent's onGoToMonth. */
    const defaultActive = useMemo(() => {
        if (selected && isSameMonth(selected, visibleMonth)) return selected
        if (isSameMonth(today, visibleMonth)) return today
        return startOfMonth(visibleMonth)
    }, [selected, today, visibleMonth])
    const [activeIso, setActiveIso] = useState<string>(() => toIso(defaultActive))
    const dayRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
    const hasFocusedOnceRef = useRef(false)

    // Re-anchor the active date when the visible month changes (e.g. chevron click).
    useEffect(() => {
        const current = parseValue(activeIso)
        if (!current || !isSameMonth(current, visibleMonth)) {
            setActiveIso(toIso(defaultActive))
        }
    }, [defaultActive, visibleMonth, activeIso])

    // Move DOM focus to the active cell — but only after the user has begun
    // interacting via the keyboard (or the popover already gave us focus).
    // Without the guard we'd steal focus on mount and force the calendar open
    // any time it renders (e.g., a date filter component re-renders).
    useEffect(() => {
        if (!hasFocusedOnceRef.current) return
        const el = dayRefs.current.get(activeIso)
        el?.focus()
    }, [activeIso])

    const onGridKeyDown = (e: ReactKeyboardEvent<HTMLTableElement>) => {
        const current = parseValue(activeIso)
        if (!current) return
        let next: Date | null = null
        switch (e.key) {
            case 'ArrowLeft': next = addDays(current, -1); break
            case 'ArrowRight': next = addDays(current, 1); break
            case 'ArrowUp': next = addDays(current, -7); break
            case 'ArrowDown': next = addDays(current, 7); break
            case 'Home': next = startOfWeek(current); break
            case 'End': next = endOfWeek(current); break
            case 'PageUp': next = subMonths(current, 1); break
            case 'PageDown': next = addMonths(current, 1); break
            case 'Enter':
            case ' ': onSelect(activeIso); e.preventDefault(); return
            default: return
        }
        e.preventDefault()
        hasFocusedOnceRef.current = true
        if (!isSameMonth(next, visibleMonth)) {
            onGoToMonth(startOfMonth(next))
        }
        setActiveIso(toIso(next))
    }

    return (
        <>
            <div className="flex items-center justify-between pb-2">
                <div className="flex gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={labels.prevYear}
                        data-testid="calendar-prev-year"
                        onClick={onPrevYear}
                        className="h-7 w-7"
                    >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true"/>
                        <ChevronLeft className="-ml-3 h-4 w-4" aria-hidden="true"/>
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={labels.prevMonth}
                        data-testid="calendar-prev-month"
                        onClick={onPrevMonth}
                        className="h-7 w-7"
                    >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true"/>
                    </Button>
                </div>
                <button
                    type="button"
                    /* WHY: the visible "MMMM yyyy" text is the user's only on-screen
                       cue for what month the calendar is showing. With a plain
                       aria-label="Pick year" the screen reader replaces it with
                       just "Pick year", hiding the current month entirely.
                       Prepending the formatted date keeps both pieces audible. */
                    aria-label={`${format(visibleMonth, 'MMMM yyyy')}, ${labels.pickYear}`}
                    data-testid="calendar-label"
                    onClick={onPickYear}
                    className="rounded-md px-2 py-1 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {format(visibleMonth, 'MMMM yyyy')}
                </button>
                <div className="flex gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={labels.nextMonth}
                        data-testid="calendar-next-month"
                        onClick={onNextMonth}
                        className="h-7 w-7"
                    >
                        <ChevronRight className="h-4 w-4" aria-hidden="true"/>
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={labels.nextYear}
                        data-testid="calendar-next-year"
                        onClick={onNextYear}
                        className="h-7 w-7"
                    >
                        <ChevronRight className="h-4 w-4" aria-hidden="true"/>
                        <ChevronRight className="-ml-3 h-4 w-4" aria-hidden="true"/>
                    </Button>
                </div>
            </div>
            <table
                className="w-full border-collapse text-sm"
                role="grid"
                aria-label={labels.weekdayHeader}
                onKeyDown={onGridKeyDown}
            >
                <thead>
                    <tr>
                        {labels.weekdaysShort.map((wd, i) => (
                            // eslint-disable-next-line testid/require-testid -- shadcn calendar primitive: weekday header cells are decorative library internals, not page-level help anchors
                            <th
                                key={i}
                                scope="col"
                                className="h-7 w-8 text-center text-xs font-normal text-muted-foreground"
                            >
                                {wd}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({length: WEEKS_IN_GRID}, (_, w) => (
                        <tr key={w}>
                            {gridDays.slice(w * DAYS_IN_WEEK, (w + 1) * DAYS_IN_WEEK).map((d, i) => {
                                const iso = toIso(d)
                                const isCurrentMonth = isSameMonth(d, visibleMonth)
                                const isSelected = selected != null && isSameDay(d, selected)
                                const isToday = isSameDay(d, today)
                                const isActive = iso === activeIso
                                return (
                                    <td key={i} className="p-0 text-center">
                                        <button
                                            type="button"
                                            data-testid={`calendar-day-${iso}`}
                                            aria-pressed={isSelected}
                                            aria-label={format(d, 'PPP')}
                                            tabIndex={isActive ? 0 : -1}
                                            ref={(el) => {
                                                if (el) dayRefs.current.set(iso, el)
                                                else dayRefs.current.delete(iso)
                                            }}
                                            onClick={() => onSelect(iso)}
                                            onFocus={() => { hasFocusedOnceRef.current = true; setActiveIso(iso) }}
                                            className={cn(
                                                'h-8 w-8 rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                                !isCurrentMonth && 'text-muted-foreground/50',
                                                isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                                                !isSelected && isToday && 'border border-primary/50',
                                                !isSelected && 'hover:bg-accent',
                                            )}
                                        >
                                            {d.getDate()}
                                        </button>
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    )
}

interface YearViewProps {
    visibleMonth: Date
    selectedYear: number | null
    todayYear: number
    labels: CalendarLabels
    onShiftDecade: (delta: number) => void
    onPickYear: (year: number) => void
    onBackToDays: () => void
}

/**
 * Issue 342.26 — 4×3 grid of years anchored on the visible year (centered on
 * row 1). Decade chevrons step by 12 years (one page) so a far-past birth
 * year is reachable in a few clicks.
 */
function YearView({visibleMonth, selectedYear, todayYear, labels, onShiftDecade, onPickYear, onBackToDays}: YearViewProps) {
    const anchorYear = visibleMonth.getFullYear()
    // Render anchor year on the top row (relative offset 0..3), then offsets
    // 4..7 on row 2, 8..11 on row 3 — anchor sits at top-left so the grid
    // always shows {anchor … anchor + 11}. Decade chevrons jump by ±12.
    const startYear = anchorYear
    const years: number[] = []
    for (let i = 0; i < YEARS_PER_PAGE; i++) {
        years.push(startYear + i)
    }
    return (
        <>
            <div className="flex items-center justify-between pb-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={labels.prevDecade}
                    data-testid="calendar-prev-decade"
                    onClick={() => onShiftDecade(-YEARS_PER_PAGE)}
                    className="h-7 w-7"
                >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true"/>
                </Button>
                <button
                    type="button"
                    aria-label={labels.backToDays}
                    data-testid="calendar-year-range"
                    onClick={onBackToDays}
                    className="rounded-md px-2 py-1 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    {`${startYear}–${startYear + YEARS_PER_PAGE - 1}`}
                </button>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={labels.nextDecade}
                    data-testid="calendar-next-decade"
                    onClick={() => onShiftDecade(YEARS_PER_PAGE)}
                    className="h-7 w-7"
                >
                    <ChevronRight className="h-4 w-4" aria-hidden="true"/>
                </Button>
            </div>
            <table className="w-full border-collapse text-sm" role="grid" aria-label={labels.pickYear}>
                <tbody>
                    {Array.from({length: YEAR_GRID_ROWS}, (_, r) => (
                        <tr key={r}>
                            {years.slice(r * YEAR_GRID_COLUMNS, (r + 1) * YEAR_GRID_COLUMNS).map(year => {
                                const isSelected = selectedYear === year
                                const isToday = todayYear === year
                                return (
                                    <td key={year} className="p-1 text-center">
                                        <button
                                            type="button"
                                            data-testid={`calendar-year-${year}`}
                                            aria-pressed={isSelected}
                                            onClick={() => onPickYear(year)}
                                            className={cn(
                                                'h-9 w-full rounded-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                                isSelected && 'bg-primary text-primary-foreground hover:bg-primary/90',
                                                !isSelected && isToday && 'border border-primary/50',
                                                !isSelected && 'hover:bg-accent',
                                            )}
                                        >
                                            {year}
                                        </button>
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    )
}

/** Apply a year to a date while keeping month and day-of-month intact. */
function withYear(d: Date, year: number): Date {
    const next = new Date(d)
    next.setFullYear(year)
    return next
}
